import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormTextArea } from '@/components/forms/form-text-area';
import { Button } from '@/components/ui/button';
import { DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/hooks/use-toast';
import { createClassTime, getClassTimesOfSchedule, updateClassTime } from '@/lib/api-calls';
import { isNotErrorString } from '@/lib/common-utils';
import { parseIntoMeetingBlock } from '@/lib/date-utils';
import { textAreaValueToArray } from '@/lib/string-utils';
import { formatMeetingBlockInput } from '@/routes/_layout/scheduling/schedules/-components/formatting';
import { MinimalClassTime, UpdateClassTimeOptions } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

interface CreateEditClassTimeProps {
  trigger?: ReactNode;
  modalTitle?: string;
  from?: {
    fromClassTime: MinimalClassTime;
    mode: 'edit' | 'create';
  };
  afterSave?: () => void;
  schoolTimeZone: string;
}

const createEditClassTimeFormSchema = z.object({
  classTimeBreaks: z
    .string()
    .optional()
    .superRefine((input, ctx) => {
      const classTimeBreaks = textAreaValueToArray(input);
      if (!classTimeBreaks.length) {
        return;
      }

      for (const classTimeBreak of classTimeBreaks) {
        const result = parseIntoMeetingBlock(classTimeBreak);
        if (typeof result === 'string') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: result,
          });
          return;
        }
      }
    }),
  studentSeats: z.coerce
    .number({ message: 'Please enter a valid number.' })
    .int({ message: 'Please enter an integer number.' })
    .min(0)
    .default(0),
});

const toDefaultValue = (
  fromClassTime?: MinimalClassTime,
): DefaultValues<z.infer<typeof createEditClassTimeFormSchema>> => {
  return {
    classTimeBreaks: (fromClassTime?.meetingBlocks || []).map(formatMeetingBlockInput).join('\n'),
    studentSeats: fromClassTime?.studentSeats ?? 0,
  };
};

function getClassTimeHash(classTime?: MinimalClassTime): string {
  if (!classTime) return 'new';
  return `${classTime.id || ''}-${classTime.studentSeats ?? 0}-${(classTime.meetingBlocks || []).map((b) => JSON.stringify(b)).join('|')}`;
}

export function CreateEditClassTime({
  trigger,
  modalTitle,
  from,
  afterSave,
  schoolTimeZone,
}: CreateEditClassTimeProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync: createClassTimeMutate, isPending: isCreating } = useMutation({
    mutationFn: createClassTime,
  });
  const { mutateAsync: updateClassTimeMutate, isPending: isUpdating } = useMutation({
    mutationFn: updateClassTime,
  });

  const fromClassTime = from?.fromClassTime;
  const defaultValues = toDefaultValue(fromClassTime);

  const formKey = getClassTimeHash(fromClassTime);

  const onSubmit = ({ formData, reset, closeModal }: SubmitProps<z.infer<typeof createEditClassTimeFormSchema>>) => {
    if (isCreating || isUpdating) return;

    const classTimeBreaks = textAreaValueToArray(formData.classTimeBreaks)
      .map((s) => parseIntoMeetingBlock(s))
      .filter(isNotErrorString);

    if (!fromClassTime?.scheduleId) {
      toast({ title: 'Missing schedule ID' });
      return;
    }

    const payload: UpdateClassTimeOptions = {
      meetingBlocks: classTimeBreaks,
      type: 'recurring',
      studentSeats: formData.studentSeats,
    };

    const apiCall =
      from && from.mode === 'edit'
        ? () =>
            updateClassTimeMutate({
              id: fromClassTime.id,
              data: payload,
            })
        : () =>
            createClassTimeMutate({
              scheduleId: fromClassTime.scheduleId,
              ...payload,
            });

    return apiCall()
      .then(() => {
        toast({
          title: from?.mode === 'edit' ? 'Class time updated' : 'Class time(s) added',
        });
        reset();
        closeModal();
        afterSave?.();
        queryClient.invalidateQueries(getClassTimesOfSchedule(fromClassTime.scheduleId));
      })
      .catch((err) => {
        toast({
          title: from?.mode === 'edit' ? 'Class time update failed' : 'Failed to add class time(s)',
          description: err?.message,
        });
      });
  };

  return (
    <FormModal
      key={formKey}
      modalTitle={modalTitle || 'Add class time'}
      trigger={trigger || <Button>Create class time</Button>}
      onSubmit={onSubmit}
      submitButtonProps={{
        disabled: isCreating || isUpdating,
      }}
      formSchema={createEditClassTimeFormSchema}
      dialogContentProps={{ className: 'max-w-2xl' }}
      defaultValues={defaultValues}
    >
      {({ form }) => {
        return (
          <div className="flex flex-col gap-2">
            <div className="mt-2 text-red-700 text-lg">
              The class time you enter here is in school's time zone:{' '}
              <span className="font-bold">{schoolTimeZone}</span>
            </div>
            <DialogDescription>
              Class times (One class time per line), format: 'Day of week, HH:MM - HH:MM'
            </DialogDescription>
            <div className="col-span-2 mb-3">
              <FormTextArea
                name="classTimeBreaks"
                form={form}
                textAreaProps={{
                  placeholder: 'e.g. Monday, 12:00 - 14:00',
                }}
                label={''}
              />
            </div>
            <FormInputField
              name="studentSeats"
              label="Student seats"
              form={form}
              inputProps={{
                type: 'number',
                className: 'w-full',
                min: 0,
              }}
            />
          </div>
        );
      }}
    </FormModal>
  );
}
